<template>
	<div class="dialog draggable-item bg-light light w-100-sm w-100-md w-75-lg w-50-xl h-75">
		<div class="dialog-title pl-10">
			{{ !team.id ? $t('Create new team') : team.name }}
			<span class="button square closer" @click="$root.$emit('CLOSETEAMDIALOG')"></span>
		</div>
		<div class="dialog-content p-3 overflow">
			<div class="form-group">
				<label class="text-bold mb-1 d-block">{{ $t("Name") }} <span class="fg-red">*</span></label>
				<input class="input w-100 mb-2" v-model="team.name" maxlength="100" />
			</div>
			<div class="form-group">

				<!-- MEMBRI: select multipla Metro UI -->
				<h6 class="border-bottom pb-1 mt-3 text-bold">{{ $t("Members") }} <span class="fg-red">*</span></h6>
				<select v-if="users && users.length > 0" data-role="select" data-filter="true" multiple :data-filter-placeholder="$t('Search for users')" data-cls-selected-item="bg-light border pr-2 rounded" data-cls-selected-item-remover="bg-transparent border-0 d-flex flex-align-center" class="input" id="teamuserselect" name="ids_users" ref="usersSelect" @change="onUsersChange">
					<option v-for="u in users" :value="u.id" :key="u.id" :selected="isUserSelected(u.id)">
						{{ u.username }}
					</option>
				</select>
				<div v-else class="text-small fg-dark mb-2">{{ $t("No users available") }}</div>

				<!-- Tabella membri selezionati con flag is_lead -->
				<table v-if="team.users.length != 0" class="table compact w-100 mb-2 mt-2">
					<thead>
						<tr>
							<th>{{ $t("User") }}</th>
							<th class="text-center">{{ $t("Team Leader") }}</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="m in team.users" :key="m.id_user">
							<td>{{ usersMap[m.id_user].username }}</td>
							<td class="text-center">
								<input type="radio" name="team_lead" :checked="m.is_lead" @change="selectLead(m.id_user)" />
							</td>
						</tr>
					</tbody>
				</table>

				<h6 class="border-bottom pb-1 mt-3 text-bold">{{ $t("Assigned groups") }}</h6>
				<select v-if="groups && groups.length > 0" data-role="select" data-filter="true" multiple :data-filter-placeholder="$t('Search for groups')" data-cls-selected-item="bg-light border pr-2 rounded" data-cls-selected-item-remover="bg-transparent border-0 d-flex flex-align-center" class="input" id="teamgroupselect" name="ids_groups" ref="groupsSelect" @change="onGroupsChange">
					<option v-for="g in groups" :value="g.id_group" :key="g.id_group" :selected="isGroupSelected(g.id_group)">
						{{ g.name_group }}
					</option>
				</select>
				<div v-else class="text-small fg-dark mb-2">{{ $t("No groups available") }}</div>
			</div>
		</div>
		<div class="dialog-actions p-3 border-top text-right">
			<button class="button" @click="$root.$emit('CLOSETEAMDIALOG')">{{ $t("Cancel") }}</button>
			<button class="button secondary ml-2" :disabled="!canSave" v-disable-tooltip:saveButton_disabled_message @click="save">
				{{ $t("Save") }}
			</button>
		</div>
	</div>
</template>

<script>
export default {
	name: "TeamDialog",
	props: {
		team: {
			type: Object,
			required: true
		}
	},
	data() {
		return {
			users: [],
			groups: [],
			usersMap: null,
			saveButton_disabled_message: ""
		};
	},
	computed: {
		canSave() {
			if (this.team.name == "") {
				this.saveButton_disabled_message = this.$t("Insert a name");
				return false;
			}

			if (this.team.users.length == 0) {
				this.saveButton_disabled_message = this.$t("Select at least one member");
				return false;
			}

			if (this.team.users.filter(m => m.is_lead).length == 0) {
				this.saveButton_disabled_message = this.$t("Set a member as team leader");
				return false;
			}
			
			this.saveButton_disabled_message = "";
			return true;
		}
	},
	created() {
		this.team.users = this.team.users || [];
		this.users = this.$session.getUsers() || [];
		this.groups = this.$session.getGroups() || [];
		this.usersMap = this.users.toMap("id");
		this.normalizeLeadSelection();
	},
	methods: {
		// team.users contiene solo {id_user, is_lead} — lo username si recupera dalla cache utenti
		isUserSelected(id) {
			return !!(this.team.users || []).find(m => m.id_user == id);
		},
		isGroupSelected(id) {
			return this.team.groups.some(idGroup => idGroup == id);
		},
		onUsersChange(e) {
			// Metro UI mantiene la <select> nativa sincronizzata: leggiamo i valori da selectedOptions.
			// team.users contiene solo {id_user, is_lead} — lo username NON viene memorizzato
			// (verrà risolto in UI con usernameOf()).
			const sel = e.target;
			const ids = Array.from(sel.selectedOptions).map(o => o.value);
			const existing = this.team.users || [];
			this.team.users = ids.map(id => {
				// Mantengo is_lead se l'utente era già selezionato
				const prev = existing.find(m => m.id_user == id);
				if (prev) return { id_user: prev.id_user, is_lead: !!prev.is_lead };
				return { id_user: id, is_lead: false };
			});
			this.normalizeLeadSelection();
		},
		onGroupsChange(e) {
			const sel = e.target;
			this.team.groups.update(Array.from(sel.selectedOptions).map(o => o.value));
		},
		selectLead(idUser) {
			this.team.users = (this.team.users || []).map(m => ({
				id_user: m.id_user,
				is_lead: m.id_user == idUser
			}));
		},
		normalizeLeadSelection() {
			let foundLead = false;
			this.team.users = (this.team.users || []).map(m => {
				const isLead = !!m.is_lead && !foundLead;
				if (isLead) foundLead = true;
				return { id_user: m.id_user, is_lead: isLead };
			});
		},

		async save() {
			if (!this.canSave) return;
			let saved;
			if (this.team.id) {
				saved = await this.$api.editTeam(this.team);
			} else {
				saved = await this.$api.createTeam(this.team);
			}
			if (saved) {
				this.$root.$emit("refreshTeams");
				this.$root.$emit("CLOSETEAMDIALOG");
			}
		}
	}
}

</script>

<style scoped>
.list-group {
	list-style: none;
	padding: 0;
	margin: 0;
}

.flex-1 {
	flex: 1;
}
</style>
